import { PickerDisplayMode, PickerResponse } from 'filestack-js/build/main/lib/picker';
import React, { useEffect, useRef, useState, FunctionComponent, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useFetch } from '@api/ApiHelper';
import { CustomFieldKeysResponseObject } from '@api/v4/resources/CustomFieldKeysTypes';
import { CustomFieldValue } from '@api/v4/assets/customFieldTypes';
import { StructuredFileMetadata } from '@api/uploaders';
import { AssetCreateBodyAttributes, AssetsResponse } from '@api/v4/assets/assetTypes';
import { useFilestackPicker } from '@components/common/custom_hooks/useFilestackPicker';
import { addFiles } from '@components/common/filestack_uploader/helpers';
import { BFLoader } from '@components/common/loader/main';
import { StandardDialog, DialogSizes } from '@components/library/dialog';
import { FontIcons } from '@components/library/icon';
import { PrimaryButton, TertiaryButton } from '@components/library/button';
import {
  CustomFieldKeyValueRow
} from '@components/asset/modal/tabs/edit/main_pane/custom_fields/custom-field-row/CustomFieldKeyValueRow';
import { FlattenedCustomFieldKeyValuesMap } from '@components/asset/modal/tabs/edit/EditTabTypes';
import {
  isMissingCustomFieldValue,
  putCustomFields
} from '@components/show_page/sections/asset/required-custom-fields-dialog-helper';
import { removeFileExtension, removeUnderscores } from '@helpers/humanize';
import { track as trackLogRocket, TrackedEvent } from '@helpers/logRocket';

interface RequiredCustomFieldsDialogProps {
  libraryName: string;
  onNewAssets: (response: AssetsResponse) => void;
  onAssetProcessing: (quantity: number ) => void;
  requiredCustomFieldKeys: Array<CustomFieldKeysResponseObject>;
  sectionKey: string;
  setShowRequiredCustomFieldsDialog: SetStateDispatch<boolean>;
  showRequiredCustomFieldsDialog: boolean;
  activeLabelKey?: string;
}

const createEmptyCustomFieldMap = (
  customFieldKeys: CustomFieldKeysResponseObject[]
): FlattenedCustomFieldKeyValuesMap => {
  const map: FlattenedCustomFieldKeyValuesMap = {};
  customFieldKeys.forEach((customFieldKey) => {
    map[customFieldKey.id] = {
      customFieldKey: {
        id: customFieldKey.id,
        name: customFieldKey.attributes.name
      },
      customFieldValues: [],
      customFieldTouched: false
    };
  });
  return map;
};

const cleanName = (filename: string): string => removeUnderscores(removeFileExtension(filename)).trim();

enum RequiredCustomFieldsDialogSteps {
  CloseWarning,
  CreatingAssets,
  CustomFields,
  Upload
}

export const RequiredCustomFieldsDialog: FunctionComponent<RequiredCustomFieldsDialogProps> = ({
  activeLabelKey,
  libraryName,
  onNewAssets,
  onAssetProcessing,
  requiredCustomFieldKeys,
  sectionKey,
  setShowRequiredCustomFieldsDialog,
  showRequiredCustomFieldsDialog
}) => {
  const [customFieldsMap, setCustomFieldsMap] = useState<FlattenedCustomFieldKeyValuesMap>(createEmptyCustomFieldMap(requiredCustomFieldKeys));
  const [step, setStep] = useState(RequiredCustomFieldsDialogSteps.CustomFields);
  const [files, setFiles] = useState<Record<string, StructuredFileMetadata[]>>({});

  const assetCreateBodyAttributes = (): AssetCreateBodyAttributes[] => {
    const fileIndices = Object.keys(files);
    return fileIndices.map((fileIndex) => ({
      attachments: [...files[fileIndex]],
      ...activeLabelKey && { labels: [activeLabelKey] },
      name: cleanName(files[fileIndex][0].filename)
    }));
  };

  const createAssetsFetch = useFetch<AssetsResponse>({
    url: `/api/v4/${BFG.resource.type}s/${BFG.resource.key}/assets?fast_jsonapi=true&queue_priority=high`,
    fetchOnMount: false,
    method: 'POST',
    body: {
      data: {
        attributes: assetCreateBodyAttributes()
      },
      section_key: sectionKey // eslint-disable-line @typescript-eslint/naming-convention
    }
  });

  const listContainerRef = useRef(null);
  const instructionsCopy = bfTranslate(`Please complete the following required Custom Fields before uploading assets to this ${libraryName}.`);
  const tempValuePrefix = 'create-cfv';

  const onUpload = (filestackFiles: StructuredFileMetadata[] | Record<string, StructuredFileMetadata[]>): void => {
    if (Array.isArray(filestackFiles)) {
      const mappedFiles = {};
      filestackFiles.forEach((filestackFile, i) => {
        mappedFiles[i] = [filestackFile];
      });
      setFiles(mappedFiles);
    } else {
      // files with the same name have been merged
      setFiles(filestackFiles);
    }
  };

  const onUploadDone = (pickerResponse: PickerResponse): void => {
    addFiles(pickerResponse, onUpload);
  };

  const { loading, picker, render } = useFilestackPicker({
    onUploadDone,
    pickerOptions: {
      container: '.required-custom-fields-dialog__step-2',
      displayMode: PickerDisplayMode.inline
    }
  });

  useEffect(() => {
    if (!showRequiredCustomFieldsDialog) {
      setCustomFieldsMap(createEmptyCustomFieldMap(requiredCustomFieldKeys));
      setStep(RequiredCustomFieldsDialogSteps.CustomFields);
    }
  }, [showRequiredCustomFieldsDialog]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (requiredCustomFieldKeys.some((requiredKey, i) => requiredKey.id !== Object.keys(customFieldsMap)[i])) {
      setCustomFieldsMap(createEmptyCustomFieldMap(requiredCustomFieldKeys));
    }
  }, [requiredCustomFieldKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step === RequiredCustomFieldsDialogSteps.Upload) {
      render();
    } else if (picker) {
      picker.close();
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (picker) {
      picker.open();
    }
  }, [picker]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (Object.keys(files).length > 0) {
      onAssetProcessing(Object.keys(files).length);
      createAssetsFetch.fetch();
      setStep(RequiredCustomFieldsDialogSteps.CreatingAssets);
    }
  }, [files]); // eslint-disable-line react-hooks/exhaustive-deps

  const beforeClose = (forceClose?: boolean): boolean => {
    const hasTouched = requiredCustomFieldKeys.find((requiredKey) => (
      customFieldsMap[requiredKey.id].customFieldTouched
    ));

    if (!forceClose && hasTouched && step !== RequiredCustomFieldsDialogSteps.CloseWarning) {
      if (picker) {
        picker.close();
      }
      setStep(RequiredCustomFieldsDialogSteps.CloseWarning);
      return false;
    }

    setShowRequiredCustomFieldsDialog(false);
    return true;
  };

  useEffect(() => {
    if (createAssetsFetch.response) {
      const assetKeys = createAssetsFetch.response.data.map((assetResponse) => assetResponse.id);
      const customFieldPromises = putCustomFields(requiredCustomFieldKeys, customFieldsMap, assetKeys);
      onNewAssets(createAssetsFetch.response);
      onAssetProcessing(0);
      Promise.all(customFieldPromises).catch(() => {
        // note that Promise.all returns a single error as soon
        // as one promise is rejected, if we want
        // more granular error tracking on the failures
        // we'll need to handle each request individually
        Notify.create({
          type: 'error',
          title: bfTranslate('There was a problem applying Custom Fields to your Assets. Please update them manually.')
        });
        trackLogRocket(TrackedEvent.RequiredCustomFieldsCreateError);
      }).finally(() => {
        // we show an error when there's a problem but still close the dialog
        beforeClose(true /* forceClose */);
      });
    }
  }, [createAssetsFetch.response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (createAssetsFetch.error) {
      // display error
      Notify.create({
        type: 'error',
        title: bfTranslate('There was a problem uploading your files. Please try again or contact support.')
      });
      trackLogRocket(TrackedEvent.RequiredCustomFieldsAssetUploadError);
      // reset file state but not custom fields
      setFiles({});
      // go back to uploader
      setStep(RequiredCustomFieldsDialogSteps.Upload);
      onAssetProcessing(0);
    }
  }, [createAssetsFetch.error]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = (
    customFieldKeyId: string,
    customFieldValue: CustomFieldValue
  ): void => {
    const customFieldsMapCopy = { ...customFieldsMap };

    customFieldsMapCopy[customFieldKeyId] = {
      ...customFieldsMap[customFieldKeyId],
      customFieldValues: [
        ...customFieldsMap[customFieldKeyId].customFieldValues,
        {
          key: `${tempValuePrefix}-${uuidv4()}`,
          value: customFieldValue?.value || ''
        }
      ],
      customFieldTouched: true
    };

    setCustomFieldsMap(customFieldsMapCopy);
  };

  const genHandleCreate = (customFieldKeyId: string) => (customFieldValue: CustomFieldValue): void => {
    handleCreate(customFieldKeyId, customFieldValue);
  };

  const genHandleDelete = (customFieldKeyId: string) => (customFieldValue: CustomFieldValue): void => {
    const customFieldsMapCopy = { ...customFieldsMap };
    const valuesCopy: CustomFieldValue[] = [];
    customFieldsMap[customFieldKeyId].customFieldValues.forEach((cfv) => {
      if (cfv.key !== customFieldValue.key) {
        valuesCopy.push(cfv);
      }
    });
    customFieldsMapCopy[customFieldKeyId] = {
      ...customFieldsMap[customFieldKeyId],
      customFieldValues: valuesCopy,
      customFieldTouched: true
    };
    setCustomFieldsMap(customFieldsMapCopy);
  };

  const genHandleUpdate = (customFieldKeyId: string) => (customFieldValue: CustomFieldValue): void => {
    if (!customFieldValue.key) {
      return handleCreate(customFieldKeyId, customFieldValue);
    }

    const customFieldsMapCopy = { ...customFieldsMap };
    const valuesCopy = customFieldsMapCopy[customFieldKeyId].customFieldValues.map((cfv) => {
      if (cfv.key === customFieldValue.key) {
        return customFieldValue;
      }

      return cfv;
    });

    // clear out both value AND the temp key if the value is empty
    const resetSingleEntryValues = valuesCopy.length === 1 && !valuesCopy[0].value;
    customFieldsMapCopy[customFieldKeyId] = {
      ...customFieldsMap[customFieldKeyId],
      customFieldValues: resetSingleEntryValues ? [] : valuesCopy,
      customFieldTouched: true
    };

    return setCustomFieldsMap(customFieldsMapCopy);
  };

  const handleRemoveValues = (removeCustomFieldKeyId: string): void => {
    const customFieldsMapCopy = { ...customFieldsMap };
    customFieldsMapCopy[removeCustomFieldKeyId] = {
      ...customFieldsMap[removeCustomFieldKeyId],
      customFieldValues: []
    };
    setCustomFieldsMap(customFieldsMapCopy);
  };

  const handleNextStep = (): void => {
    let missingRequiredField = false;
    const customFieldsMapCopy: FlattenedCustomFieldKeyValuesMap = {};
    requiredCustomFieldKeys.forEach((requiredKey) => {
      customFieldsMapCopy[requiredKey.id] = {
        ...customFieldsMap[requiredKey.id],
        customFieldTouched: true
      };
      if (!missingRequiredField) {
        missingRequiredField = isMissingCustomFieldValue(customFieldsMap[requiredKey.id]);
      }
    });

    if (!missingRequiredField) {
      setStep(RequiredCustomFieldsDialogSteps.Upload);
    } else {
      setCustomFieldsMap(customFieldsMapCopy);
    }
  };

  const renderCustomFieldsBody = (): ReactNode => (
    <>
      <p className="required-custom-fields-dialog__instructions">{instructionsCopy}</p>
      <>
        <header className="custom-fields-list--heading">
          <div className="custom-fields-list--heading--key"><p>{bfTranslate('Key')}</p></div>
          <div className="custom-fields-list--heading--values"><p>{bfTranslate('Values')}</p></div>
        </header>
        <ul
          ref={listContainerRef}
          className="custom-field-list bf-scroll-element"
        >
          {requiredCustomFieldKeys && requiredCustomFieldKeys.map(({ attributes, id }) => (
            <CustomFieldKeyValueRow
              key={id}
              controlledCustomFieldsEnabled
              customFieldKey={{
                attributes,
                id,
                type: 'custom_field_keys'
              }}
              existingCustomFieldMap={customFieldsMap[id]}
              handleCreate={genHandleCreate(id)}
              handleDelete={genHandleDelete(id)}
              handleRemoveValues={handleRemoveValues}
              handleUpdate={genHandleUpdate(id)}
              overflowParentRef={listContainerRef}
              // Always say translating is false
              // whatever locale they are adding in will
              // create in the default locale as well
              translating={false}
            />
          ))}
        </ul>
      </>
    </>
  );

  const renderCustomFieldsFooter = (): ReactNode => (
    <div className="required-custom-fields-dialog__next-step--container">
      <PrimaryButton
        className="required-custom-fields-dialog__next-step"
        onClick={handleNextStep}
        size="small"
      >
        {bfTranslate('Next Step')}
      </PrimaryButton>
    </div>
  );

  const renderCloseWarningBody = (): ReactNode => (
    <div className="required-custom-fields-dialog__close-warning--body">
      <h3 className="required-custom-fields-dialog__close-warning--title">
        {bfTranslate('Are you sure you want to cancel your upload?')}
      </h3>
      <p className="required-custom-fields-dialog__close-warning--content">
        {bfTranslate(`Custom Fields are required to upload new assets into this ${libraryName}.`)}
      </p>
    </div>
  );

  const renderCloseWarningFooter = (): ReactNode => (
    <div className="required-custom-fields-dialog__close-warning--footer">
      <TertiaryButton
        className="required-custom-fields-dialog__close-warning--button"
        onClick={(): boolean => beforeClose(true)}
        size="small"
      >
        {bfTranslate('Yes, cancel upload')}
      </TertiaryButton>
      <PrimaryButton
        className="required-custom-fields-dialog__close-warning--button"
        onClick={(): void => setStep(RequiredCustomFieldsDialogSteps.CustomFields)}
        size="small"
      >
        {bfTranslate('No, return to Custom Fields')}
      </PrimaryButton>
    </div>
  );

  const renderUpload = (): ReactNode => (
    <div className={`required-custom-fields-dialog__step-2${loading ? ' required-custom-fields-dialog__step-2--loading' : ''}`}>
      {loading && <BFLoader />}
    </div>
  );

  const renderCreatingAssets = (): ReactNode => (
    <div className="required-custom-fields-dialog__creating-assets">
      <BFLoader />
      <p>{bfTranslate('Please wait while upload completes.')}</p>
    </div>
  );

  const renderStepBodies = (): ReactNode => {
    switch (step) {
      case RequiredCustomFieldsDialogSteps.CloseWarning:
        return renderCloseWarningBody();
      case RequiredCustomFieldsDialogSteps.Upload:
        return renderUpload();
      case RequiredCustomFieldsDialogSteps.CreatingAssets:
        return renderCreatingAssets();
      default:
        return renderCustomFieldsBody();
    }
  };

  const renderStepFooters = (): ReactNode => {
    switch (step) {
      case RequiredCustomFieldsDialogSteps.CloseWarning:
        return renderCloseWarningFooter();
      case RequiredCustomFieldsDialogSteps.CustomFields:
        return renderCustomFieldsFooter();
      default:
        return null;
    }
  };

  const dialogClassName = (): string => {
    if (step === RequiredCustomFieldsDialogSteps.Upload) {
      return 'required-custom-fields-dialog required-custom-fields-dialog--upload';
    }

    return 'required-custom-fields-dialog';
  };

  const dialogTitle = (): string | undefined => {
    switch (step) {
      case RequiredCustomFieldsDialogSteps.CustomFields:
        return bfTranslate('Add required Custom Fields');
      case RequiredCustomFieldsDialogSteps.Upload:
        return bfTranslate('Upload files');
      default:
        return undefined;
    }
  };

  const titleIcon = (): string | undefined => {
    switch (step) {
      case RequiredCustomFieldsDialogSteps.CustomFields:
        return `bff-${FontIcons.CustomField}`;
      case RequiredCustomFieldsDialogSteps.Upload:
        return `bff-${FontIcons.Ingest}`;
      default:
        return undefined;
    }
  };

  const dialogSize = (): DialogSizes => {
    switch (step) {
      case RequiredCustomFieldsDialogSteps.CreatingAssets:
        return DialogSizes.Xxsmall;
      case RequiredCustomFieldsDialogSteps.CloseWarning:
        return DialogSizes.Medium;
      default:
        return DialogSizes.Large;
    }
  };

  const noFooterSteps = [RequiredCustomFieldsDialogSteps.Upload, RequiredCustomFieldsDialogSteps.CreatingAssets];
  const noTitleSteps = [RequiredCustomFieldsDialogSteps.CloseWarning, RequiredCustomFieldsDialogSteps.CreatingAssets];

  return (
    <StandardDialog
      beforeClose={beforeClose}
      contentCentered={step === RequiredCustomFieldsDialogSteps.CloseWarning}
      dialogClassName={dialogClassName()}
      dialogHeaderClassName={step === RequiredCustomFieldsDialogSteps.CreatingAssets
        ? 'required-custom-fields-dialog--hide-header'
        : ''}
      footer={renderStepFooters()}
      id="required-custom-fields-dialog"
      open={showRequiredCustomFieldsDialog}
      setOpen={setShowRequiredCustomFieldsDialog}
      showClose={step !== RequiredCustomFieldsDialogSteps.CreatingAssets}
      showFooter={!noFooterSteps.includes(step)}
      showTitle={!noTitleSteps.includes(step)}
      size={dialogSize()}
      title={dialogTitle()}
      titleIcon={titleIcon()}
    >
      {renderStepBodies()}
    </StandardDialog>
  );
};
